import { useId } from 'react';
import { Text } from '@backstage/ui';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { stepStatusLabel, type PhaseView, type StepView } from './deletionTask';

/** The circle of a step: a check once done, a cross on failure, else its number. */
function StepMarker(props: { step: StepView }) {
  const { step } = props;
  if (step.state === 'done') {
    const Done = waadooIcons.stepDone;
    return <Done aria-hidden focusable="false" />;
  }
  if (step.state === 'failed') {
    const Failed = waadooIcons.stepFailed;
    return <Failed aria-hidden focusable="false" />;
  }
  return <span aria-hidden>{step.number}</span>;
}

function Phase(props: { phase: PhaseView }) {
  const { phase } = props;
  const titleId = useId();
  return (
    <section className="waadoo-deletion-steps__phase" aria-labelledby={titleId}>
      <Text
        as="h4"
        id={titleId}
        variant="body-x-small"
        weight="bold"
        color="secondary"
        className="waadoo-deletion-steps__phase-title"
      >
        {phase.title}
      </Text>
      <ol
        className="waadoo-deletion-steps__list"
        start={phase.steps[0]?.number}
      >
        {phase.steps.map(step => (
          <li
            key={step.id}
            className="waadoo-deletion-step"
            data-state={step.state}
            aria-current={step.state === 'running' ? 'step' : undefined}
          >
            <span className="waadoo-deletion-step__marker">
              <StepMarker step={step} />
            </span>
            <span className="waadoo-deletion-step__text">
              <Text as="span" variant="body-medium" weight="bold">
                {step.name}
              </Text>
              {step.detail && (
                <Text as="span" variant="body-small" color="secondary">
                  {step.detail}
                </Text>
              )}
            </span>
            <span
              className="waadoo-deletion-step__status"
              data-state={step.state}
            >
              {stepStatusLabel(step)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The steps of a project deletion as a vertical timeline in phases
 * (`stepPhases`): a circle per step — its number, a check once done, a
 * cross on failure — its name, the grey line of what it goes through, and
 * its status in words (never the colour alone). The running step is the
 * list's `aria-current="step"`; its ring pulses unless the reader asked for
 * reduced motion (styles.css). Each phase is an ordered list continuing the
 * numbering of the previous one.
 */
export function DeletionSteps(props: { phases: PhaseView[] }) {
  return (
    <div className="waadoo-deletion-steps">
      {props.phases.map(phase => (
        <Phase key={phase.title} phase={phase} />
      ))}
    </div>
  );
}
